/**
 * Helpers: Products
 * ------------------------------------------------------------------------------
 * Helper functions relating to product data.
 */
import { storageNamespace } from '~/helpers/storage-namespaces'
import { stringToTitleCase, stringToHandle } from '~/helpers/strings'

/**
 * When product tags use a key/value system, use this function
 * to return an array of values when presented with a given key.
 *
 * Example: Product is tagged with `colour: Red`:
 * const colour = getProductTagValueByKey(tags, 'colour')
 * Will return ['Red']
 * @param {Array} tags - List of tags.
 * @param {string} key - Product tag key.
 * @param {boolean} onlyFirstValue - If multiple tags, only return the first match.
 * @returns {Array | boolean | string}
 */
export function getProductTagValueByKey(tags = [], key, onlyFirstValue) {
  if (!Array.isArray(tags) || !key) {
    return false
  }

  const formattedKey = key.toLowerCase()

  const matchingTags = tags.filter((tag) => {
    return tag.toLowerCase().trim().startsWith(`${formattedKey}:`)
  }).map((tag) => {

    /**
     * Replace the key, rather than a .split(':') for events
     * where tags have multiple colons.
     */
    const value = tag.replace(`${key}:`, '')

    return value.trim()
  })

  if (!matchingTags.length) {
    return false
  }

  if (onlyFirstValue || matchingTags.length === 1) {
    return matchingTags[0]
  }

  return matchingTags
}

/**
 * Generate an array of strings to describe any options containing
 * international sizing values, for use as a line item property.
 * @param {Array} options - Product options with name and value properties.
 * @returns {string[]} Array of sizing property strings.
 */
export function getSizingProperties(options = []) {
  const sizingOptions =
    options.filter((option) => {
      return stringToHandle(option.name) === 'size'
    })

  return sizingOptions.map((option) => {
    return `${option.name.toLowerCase()}:${option.value}`
  })
}

/**
 * Check if a product is tagged as being restricted from the active market.
 * - These tags are used as a workaround as Global-e does not currently provide
 *   a solution for market-level restrictions using Shopify APIs.
 * @param {Array} tags
 * @returns {boolean}
 */
export function isProductMarketRestricted(tags = []) {
  if (!tags.length || !mint?.shop) {
    return false
  }

  const { continentCode, country } = mint.shop

  let userLocale = country

  /**
   * Allow for restricting products from the EU market without requiring
   * multiple tags.
   */
  const isEuMarket = continentCode === 'EU' &&
    country !== 'GB' &&
    country !== 'IE'

  if (isEuMarket) {
    userLocale = continentCode
  }

  return tags.includes(`${userLocale}_RESTRICTED`)
}

/**
 * Check if a product is unavailable both for purchasing and being added to
 * wishlists.
 * @param {object} product - Formatted product object.
 * @returns {boolean}
 */
export function isProductRestricted(product = {}) {
  return (!product?.available && !product?.wishlist?.bisEnabled) ||
    isProductMarketRestricted(product?.tags)
}

/**
 * Store an array of 'Recently Viewed' products in the browser.
 * - Typically runs on templates/product.js
 * @param {object} product - Formatted product object.
 */
export function recordProductRecentlyViewed(product) {
  const { handle } = product

  /**
   * Early return if the current product has been excluded from being stored as
   * recently viewed (e.g. gift cards).
   */
  const excludedProducts = mint.products.excludeFromRecentlyViewed

  if (excludedProducts.includes(handle)) {
    return
  }

  /**
   * Early return if the product is:
   * - out of stock and not available for back in stock alerts
   * - unavailable for the user's market
   */
  if (product.restricted) {
    return
  }

  /**
   * Sets 9 as the maximum, so if the product from the
   * current PDP are added, 8 total products still show.
   */
  const maxProducts = 9
  let currentProducts = JSON.parse(
    localStorage.getItem(storageNamespace.recentlyViewedProducts) || '[]',
  )

  /**
   * Filter out any stored products excluded from the recently viewed feature.
   */
  currentProducts = currentProducts.filter((product) => {
    return !excludedProducts.includes(product)
  })

  /**
   * If the product already exists, remove it from the array so that
   * it can be re-added to the front of the array.
   * - Assume the customer has returned to the same PDP.
   */
  if (currentProducts.includes(handle)) {
    currentProducts = currentProducts.filter((product) => {
      return product !== handle
    })
  }

  /**
   * Add to the start of the array so that it appears
   * first as the 'Most Recent' list of cards.
   */
  currentProducts.unshift(handle)

  if (currentProducts.length > maxProducts) {
    currentProducts.pop()
  }

  localStorage.setItem(
    storageNamespace.recentlyViewedProducts,
    JSON.stringify(currentProducts),
  )
}

/**
 * Generate an object of properties to set before item is added to cart.
 * @param {object} product - Formatted product object.
 * @param {object} currentVariant - Formatted variant object (currently selected).
 * @returns {object} line item properties object to set.
 */
export function getItemProperties(product, currentVariant) {
  if (!product || !currentVariant) {
    window.Log(
      'warning',
      'Error occurred while getting properties: product or variant is missing.',
      false,
      { product, currentVariant },
    )

    return null
  }

  const properties = {}
  const productType = product.types?.nsCategory || product.ns_category || false

  if (productType) {
    properties['_ns_category'] = stringToTitleCase(productType)
  }

  if (product.usesInternationalSizing && mint.shop.country !== 'GB') {
    const sizingProperties =
      getSizingProperties(currentVariant.selectedOptions)

    properties['_international_sizing'] = sizingProperties.join(',')
  }

  return properties
}

/**
 * Determines if the product is marked as `coming soon`.
 * @param {Array} tags - Shopify product tags.
 * @returns {boolean} - Whether product have `coming soon` badge.
 */
export function isComingSoonProduct(tags) {
  if (!tags?.length) {
    return false
  }

  const isComingSoon = tags.find((tag) => {
    const badgeName = tag.toLowerCase().trim()

    return (badgeName.includes('badge:') && badgeName.includes('coming soon'))
  })

  return Boolean(isComingSoon)
}
